import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  CalendarDays,
  Inbox,
  Video,
  ArrowUpRight,
} from "lucide-react";
import { useMemo, useState } from "react";
import { expandRecurringEvent, type SessionUser } from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { GoogleCalendarSyncCard } from "@/components/GoogleCalendarSyncCard";
import { AttendeeList, type Attendee } from "@/components/AttendeeList";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface CalendarEvent {
  id: string;
  kind: "ticket" | "holiday" | "company_closure" | "meeting" | "call" | "reminder" | "external";
  date: string;
  endDate?: string;
  title: string;
  url?: string;
  /** Invitados — solo presentes para eventos del propio usuario. El
   * endpoint los oculta para eventos de otros. */
  attendees?: Attendee[];
  meta?: Record<string, string>;
}

export const Route = createFileRoute("/calendar")({
  component: CalendarPage,
});

const KIND_STYLE: Record<
  CalendarEvent["kind"],
  { dot: string; chip: string; chipSelected: string; label: string }
> = {
  ticket: {
    dot: "bg-primary",
    chip: "border-primary/40 hover:bg-primary/10 text-base-content",
    chipSelected: "border-primary bg-primary text-primary-content",
    label: "Ticket",
  },
  holiday: {
    dot: "bg-warning",
    chip: "border-warning/40 hover:bg-warning/10 text-base-content",
    chipSelected: "border-warning bg-warning text-warning-content",
    label: "Feriado",
  },
  company_closure: {
    dot: "bg-error",
    chip: "border-error/40 hover:bg-error/10 text-base-content",
    chipSelected: "border-error bg-error text-error-content",
    label: "Cierre",
  },
  meeting: {
    dot: "bg-info",
    chip: "border-info/40 hover:bg-info/10 text-base-content",
    chipSelected: "border-info bg-info text-info-content",
    label: "Junta",
  },
  call: {
    dot: "bg-accent",
    chip: "border-accent/40 hover:bg-accent/10 text-base-content",
    chipSelected: "border-accent bg-accent text-accent-content",
    label: "Llamada",
  },
  reminder: {
    dot: "bg-base-content/40",
    chip: "border-base-content/30 hover:bg-base-200 text-base-content",
    chipSelected: "border-base-content bg-base-content text-base-100",
    label: "Recordatorio",
  },
  external: {
    dot: "bg-base-content/30",
    chip: "border-base-content/30 hover:bg-base-200 text-base-content",
    chipSelected: "border-base-content bg-base-content text-base-100",
    label: "Externo",
  },
};

/** Estilo "virtual" para eventos sincronizados desde Google Calendar.
 * Pisa al estilo de `external` cuando meta.source === 'google_calendar'. */
const GOOGLE_STYLE = {
  dot: "bg-success",
  chip: "border-success/40 hover:bg-success/10 text-base-content",
  chipSelected: "border-success bg-success text-success-content",
  label: "Google",
};

function effectiveStyle(ev: CalendarEvent) {
  if (ev.meta?.source === "google_calendar") return GOOGLE_STYLE;
  return KIND_STYLE[ev.kind];
}

const WEEKDAY_LABELS = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];

function CalendarPage() {
  const [anchor, setAnchor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ events: CalendarEvent[] }>({
    queryKey: ["calendar", "me"],
    queryFn: () => api.get("/calendar/me"),
  });
  const { data: me } = useQuery<SessionUser>({
    queryKey: ["me"],
    queryFn: () => api.get("/me"),
  });

  // Expandir eventos a sus ocurrencias dentro del mes visible. Soporta
  // recurrencia (daily/weekly/biweekly/monthly/yearly) leyendo los
  // campos serializados en `meta` que vienen del backend.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    // Rango del mes visible + un poco de buffer para celdas overlapping
    // con mes anterior/siguiente.
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const fromIso = `${firstOfMonth.getFullYear()}-${String(firstOfMonth.getMonth() + 1).padStart(2, "0")}-01`;
    const toIso = `${lastOfMonth.getFullYear()}-${String(lastOfMonth.getMonth() + 1).padStart(2, "0")}-${String(lastOfMonth.getDate()).padStart(2, "0")}`;
    // Pad 7 días a cada lado para celdas de mes vecino.
    const padFrom = new Date(firstOfMonth);
    padFrom.setDate(padFrom.getDate() - 7);
    const padTo = new Date(lastOfMonth);
    padTo.setDate(padTo.getDate() + 7);
    const padFromIso = padFrom.toISOString().slice(0, 10);
    const padToIso = padTo.toISOString().slice(0, 10);

    for (const e of data?.events ?? []) {
      const recurrence = e.meta?.recurrence as
        | "daily"
        | "weekly"
        | "biweekly"
        | "monthly"
        | "yearly"
        | undefined;
      const recurrenceDaysRaw = e.meta?.recurrenceDays;
      const recurrenceDays: number[] | null = recurrenceDaysRaw
        ? (JSON.parse(recurrenceDaysRaw) as number[])
        : null;
      const recurrenceUntil = e.meta?.recurrenceUntil ?? null;

      const occurrences = expandRecurringEvent(
        {
          startDate: e.date,
          endDate: e.endDate ?? e.date,
          recurrence: recurrence ?? null,
          recurrenceDays,
          recurrenceUntil,
        },
        padFromIso,
        padToIso,
      );
      for (const occ of occurrences) {
        const dates = expandRange(occ.startDate, occ.endDate);
        for (const d of dates) {
          const arr = map.get(d) ?? [];
          arr.push(e);
          map.set(d, arr);
        }
      }
    }
    void fromIso;
    void toIso;
    return map;
  }, [data?.events, anchor]);

  const selectedEvent = useMemo(
    () => (selectedId ? (data?.events.find((e) => e.id === selectedId) ?? null) : null),
    [selectedId, data?.events],
  );

  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const today = new Date();
  const todayKey = isoDate(today);

  const shift = (dir: -1 | 1) => {
    setAnchor((prev) => new Date(prev.getFullYear(), prev.getMonth() + dir, 1));
  };
  const goToday = () => {
    const t = new Date();
    setAnchor(new Date(t.getFullYear(), t.getMonth(), 1));
  };

  const monthLabel = anchor.toLocaleDateString("es-MX", { month: "long", year: "numeric" });

  return (
    // Toda la página vive en una columna flex que se estira hasta el alto
    // del viewport menos el header del shell — eso permite que el grid
    // del calendario tome el espacio sobrante sin scroll. dvh respeta la
    // barra de URL móvil dinámica.
    <div className="flex flex-col gap-3 min-h-0" style={{ height: "calc(100dvh - 6rem)" }}>
      <PageHeader
        eyebrow="calendario"
        title="Tu calendario"
        subtitle="Vence-tickets, llamadas, días libres. Una vista única de lo que viene."
        actions={
          <Link to="/" className="btn btn-ghost btn-sm">
            Volver al dashboard
          </Link>
        }
      />

      <GoogleCalendarSyncCard />

      <div className="card-elevated p-3 flex flex-wrap items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => shift(-1)}
          className="btn btn-ghost btn-sm btn-square"
          aria-label="Mes anterior"
        >
          <ChevronLeft className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => shift(1)}
          className="btn btn-ghost btn-sm btn-square"
          aria-label="Mes siguiente"
        >
          <ChevronRight className="size-4" />
        </button>
        <button type="button" onClick={goToday} className="btn btn-ghost btn-sm">
          Hoy
        </button>
        <h3 className="font-display font-semibold text-lg capitalize ml-2">{monthLabel}</h3>
        {/* Leyenda completa solo en xl+; en mobile/tablet ocupaba mucho
            espacio y empujaba al título a otra línea. */}
        <div className="ml-auto hidden xl:flex flex-wrap items-center gap-3 text-xs text-base-content/60">
          {Object.entries(KIND_STYLE).map(([k, s]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={`size-2 rounded-full ${s.dot}`} aria-hidden />
              {s.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <span className={`size-2 rounded-full ${GOOGLE_STYLE.dot}`} aria-hidden />
            {GOOGLE_STYLE.label}
          </span>
        </div>
      </div>

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {/* Layout principal: en lg+ el calendario ocupa 2/3 y el panel de
          detalles el tercio derecho. En pantallas chicas se apilan: el
          panel queda abajo. `flex-1 min-h-0` deja que tome el alto que
          sobra después de header + filtros. */}
      {!isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 flex-1 min-h-0">
          {/* En mobile/sm el grid mensual queda apretado (7 columnas con
              chips no caben en 375px). Mostramos una vista agenda:
              lista de días del mes con eventos debajo. md+ usa el grid
              tradicional. */}
          <div className="card-elevated overflow-hidden flex-col min-h-0 hidden md:flex">
            <div className="grid grid-cols-7 text-[10px] font-semibold uppercase tracking-wider text-base-content/55 border-b border-base-300/60 shrink-0">
              {WEEKDAY_LABELS.map((d) => (
                <div key={d} className="px-2 py-1.5 text-center">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 grid-rows-6 flex-1 min-h-0">
              {grid.map((cell) => {
                const key = `${cell.year}-${String(cell.month + 1).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`;
                const events = eventsByDate.get(key) ?? [];
                const isCurrentMonth = cell.month === anchor.getMonth();
                const isToday = key === todayKey;
                return (
                  <div
                    key={key}
                    className={
                      "px-1.5 py-1 border-r border-b border-base-300/40 flex flex-col gap-0.5 min-h-0 overflow-hidden " +
                      (isCurrentMonth ? "" : "bg-base-200/30 text-base-content/40")
                    }
                  >
                    <div className="flex items-center justify-between text-[11px] shrink-0">
                      <span
                        className={
                          isToday
                            ? "inline-grid place-items-center size-5 rounded-full bg-primary text-primary-content font-bold"
                            : "font-medium"
                        }
                      >
                        {cell.day}
                      </span>
                      {events.length > 0 && (
                        <span className="text-[10px] text-base-content/45">{events.length}</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-0.5 overflow-hidden">
                      {events.slice(0, 3).map((e) => {
                        const style = effectiveStyle(e);
                        const isSelected = e.id === selectedId;
                        return (
                          <button
                            key={e.id}
                            type="button"
                            onClick={() => setSelectedId(e.id)}
                            className={
                              "text-[10px] leading-tight px-1 py-0.5 rounded border text-left truncate transition-colors " +
                              (isSelected ? style.chipSelected : style.chip)
                            }
                            title={e.title}
                          >
                            <span className="inline-flex items-center gap-1 truncate w-full">
                              {!isSelected && (
                                <span
                                  className={`size-1 rounded-full ${style.dot} shrink-0`}
                                  aria-hidden
                                />
                              )}
                              <span className="truncate">{e.title}</span>
                            </span>
                          </button>
                        );
                      })}
                      {events.length > 3 && events[3] && (
                        <button
                          type="button"
                          onClick={() => setSelectedId(events[3]!.id)}
                          className="text-[10px] text-base-content/55 hover:text-primary text-left truncate"
                        >
                          +{events.length - 3} más
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Vista agenda — visible en mobile/sm, oculta en md+ */}
          <div className="card-elevated md:hidden divide-y divide-base-300/60 overflow-y-auto max-h-[calc(100dvh-16rem)]">
            {(() => {
              // Días del mes anclado que tienen al menos un evento, en
              // orden cronológico. Saltamos los días vacíos para que la
              // lista sea densa y útil. El usuario navega con prev/next
              // de meses si quiere ver otro.
              const daysWithEvents = grid.filter(
                (cell) =>
                  cell.month === anchor.getMonth() &&
                  (eventsByDate.get(
                    `${cell.year}-${String(cell.month + 1).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`,
                  )?.length ?? 0) > 0,
              );
              if (daysWithEvents.length === 0) {
                return (
                  <div className="p-6 text-center text-sm text-base-content/55">
                    Sin eventos este mes.
                  </div>
                );
              }
              return daysWithEvents.map((cell) => {
                const key = `${cell.year}-${String(cell.month + 1).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`;
                const events = eventsByDate.get(key) ?? [];
                const isToday = key === todayKey;
                const dt = new Date(`${key}T00:00:00`);
                const weekday = dt.toLocaleDateString("es-MX", { weekday: "short" });
                return (
                  <div key={key} className="px-4 py-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={
                          isToday
                            ? "inline-grid place-items-center size-7 rounded-full bg-primary text-primary-content font-bold text-sm"
                            : "inline-grid place-items-center size-7 text-sm font-semibold"
                        }
                      >
                        {cell.day}
                      </span>
                      <span className="text-xs uppercase tracking-wider text-base-content/55">
                        {weekday}
                      </span>
                    </div>
                    <div className="space-y-1.5 ml-9">
                      {events.map((e) => {
                        const style = effectiveStyle(e);
                        const isSelected = e.id === selectedId;
                        return (
                          <button
                            key={e.id}
                            type="button"
                            onClick={() => setSelectedId(e.id)}
                            className={
                              "w-full text-sm px-2.5 py-1.5 rounded border text-left transition-colors flex items-center gap-2 " +
                              (isSelected ? style.chipSelected : style.chip)
                            }
                          >
                            {!isSelected && (
                              <span
                                className={`size-1.5 rounded-full ${style.dot} shrink-0`}
                                aria-hidden
                              />
                            )}
                            <span className="truncate flex-1">{e.title}</span>
                            {e.meta?.startTime && (
                              <span className="text-[10px] tabular-nums opacity-70 shrink-0">
                                {e.meta.startTime}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          <EventDetailsPanel event={selectedEvent} me={me ?? null} />
        </div>
      )}

      {!isLoading && (data?.events ?? []).length === 0 && (
        <div className="card-elevated py-12 text-center">
          <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
            <CalendarDays className="size-6 text-primary" />
          </div>
          <h3 className="font-display font-semibold mb-1">Calendario vacío</h3>
          <p className="text-sm text-base-content/60">
            Cuando tengas tickets con fecha de entrega aparecerán acá.
          </p>
        </div>
      )}
    </div>
  );
}

function EventDetailsPanel({
  event,
  me,
}: {
  event: CalendarEvent | null;
  me: SessionUser | null;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEnableSessions = me?.role === "admin" || me?.role === "lead";
  const [scopeChoice, setScopeChoice] = useState<"single" | "series" | null>(null);
  const enableSessions = useMutation<
    { eventId: string; scope: "single" | "series" },
    Error,
    { id: string; scope: "single" | "series" }
  >({
    mutationFn: ({ id, scope }) =>
      api.post(`/calendar-events/${id}/enable-sessions`, { scope }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["calendar", "me"] });
      navigate({ to: "/events/$eventId/sessions", params: { eventId: res.eventId } });
    },
  });

  if (!event) {
    return (
      <div className="card-elevated p-6 flex flex-col items-center justify-center text-center text-base-content/55 h-full min-h-64">
        <Inbox className="size-10 mb-3 opacity-40" />
        <p className="text-sm">Click en un evento del calendario para ver sus detalles aquí.</p>
      </div>
    );
  }
  const style = effectiveStyle(event);
  const planeProject = event.meta?.projectName;
  const projectIdentifier = event.meta?.projectIdentifier;
  const panelProject = event.meta?.panelProjectName;
  const panelProjectId = event.meta?.panelProjectId;
  const state = event.meta?.state;
  const priority = event.meta?.priority;
  const startTime = event.meta?.startTime;
  const endTime = event.meta?.endTime;
  const notes = event.meta?.notes;
  const meetUrl = event.meta?.meetUrl;
  const htmlLink = event.meta?.htmlLink;
  const isGoogle = event.meta?.source === "google_calendar";
  // Datos de la session ligada (si existe): description capturada para la
  // junta + link a Mural. Lo expone /api/calendar/me como meta.session*
  // tras dedup-resolver el sessionMasterId.
  const sessionDescription = event.meta?.sessionDescription;
  const sessionMuralUrl = event.meta?.sessionMuralUrl;
  return (
    <div className="card-elevated p-5 flex flex-col gap-3 h-full min-h-64 overflow-auto">
      <div className="flex items-center gap-2">
        <span className={`size-2 rounded-full ${style.dot}`} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wider text-base-content/55">
          {style.label}
        </span>
      </div>
      <h3 className="font-display font-semibold text-lg leading-snug break-words">{event.title}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
        <dt className="text-base-content/55">Fecha</dt>
        <dd className="font-medium">
          {formatDate(event.date)}
          {startTime && (
            <span className="ml-1 text-base-content/65">
              · {startTime}
              {endTime && endTime !== startTime ? `–${endTime}` : ""}
            </span>
          )}
        </dd>
        {(panelProject || planeProject) && (
          <>
            <dt className="text-base-content/55">Proyecto</dt>
            <dd className="font-medium">
              {panelProject && panelProjectId ? (
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: panelProjectId }}
                  className="text-primary hover:underline"
                >
                  {panelProject}
                </Link>
              ) : (
                (panelProject ?? planeProject)
              )}
              {projectIdentifier && (
                <span className="ml-1.5 text-xs text-base-content/45 uppercase tracking-wider">
                  {projectIdentifier}
                </span>
              )}
              {panelProject && planeProject && panelProject !== planeProject && (
                <div className="text-xs text-base-content/45 mt-0.5">En Plane: {planeProject}</div>
              )}
            </dd>
          </>
        )}
        {state && (
          <>
            <dt className="text-base-content/55">Estado</dt>
            <dd>
              <span className="badge badge-ghost badge-sm">{state}</span>
            </dd>
          </>
        )}
        {priority && priority !== "none" && (
          <>
            <dt className="text-base-content/55">Prioridad</dt>
            <dd>
              <span className={`badge badge-sm ${priorityBadgeClass(priority)}`}>
                {priorityLabel(priority)}
              </span>
            </dd>
          </>
        )}
      </dl>
      {notes && (
        <div className="text-sm text-base-content/80 whitespace-pre-wrap leading-relaxed border-t border-base-300/60 pt-3">
          {notes}
        </div>
      )}
      {(sessionDescription || sessionMuralUrl) && (
        <div className="border-t border-base-300/60 pt-3 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-primary/80 font-semibold">
            De la sesión
          </div>
          {sessionDescription && (
            <p className="text-sm text-base-content/80 whitespace-pre-wrap leading-relaxed">
              {sessionDescription}
            </p>
          )}
          {sessionMuralUrl && (
            <a
              href={sessionMuralUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost btn-xs gap-1.5"
            >
              <ExternalLink className="size-3" /> Abrir Mural
            </a>
          )}
        </div>
      )}
      {event.attendees && event.attendees.length > 0 && (
        <div className="border-t border-base-300/60 pt-3">
          <AttendeeList attendees={event.attendees} />
        </div>
      )}
      <div className="mt-auto flex flex-wrap gap-2">
        {meetUrl && (
          <a href={meetUrl} target="_blank" rel="noreferrer" className="btn btn-success btn-sm">
            <Video className="size-3.5" /> Unirse a Meet
          </a>
        )}
        {isGoogle && htmlLink && (
          <a href={htmlLink} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
            Ver en Google Calendar <ExternalLink className="size-3.5" />
          </a>
        )}
        {!isGoogle && event.url && (
          <a href={event.url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">
            Abrir en Plane <ExternalLink className="size-3.5" />
          </a>
        )}
        {/* Histórico de sesiones para eventos del calendar del panel
            (no tickets). Para instancias sincronizadas de Google que
            están ancladas a un master del panel, navegamos al master
            (sessionMasterId) en lugar de a la instancia — la instancia
            no tiene sesiones propias. */}
        {event.kind !== "ticket" && (event.kind === "meeting" || event.meta?.sessionMasterId) && (
          <Link
            to="/events/$eventId/sessions"
            params={{ eventId: event.meta?.sessionMasterId ?? event.id }}
            className="btn btn-ghost btn-sm"
          >
            Ver histórico de sesiones <ArrowUpRight className="size-3.5" />
          </Link>
        )}
        {/* Lead/Admin pueden promover un evento del calendario (ticket
            excluido — esos son issues, no juntas) a kind=meeting para
            que aparezca en /sesiones y tenga registro de notas + Mural.
            Si el evento es recurrente preguntamos scope; sino lo
            promovemos directo. */}
        {canEnableSessions &&
          event.kind !== "ticket" &&
          event.kind !== "meeting" &&
          !event.meta?.sessionMasterId && (
            <>
              {event.meta?.isRecurring === "true" ? (
                <button
                  type="button"
                  onClick={() => setScopeChoice("single")}
                  className="btn btn-primary btn-sm"
                  disabled={enableSessions.isPending}
                >
                  {enableSessions.isPending && (
                    <span className="loading loading-spinner loading-xs" />
                  )}
                  Crear sesión
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => enableSessions.mutate({ id: event.id, scope: "single" })}
                  className="btn btn-primary btn-sm"
                  disabled={enableSessions.isPending}
                >
                  {enableSessions.isPending && (
                    <span className="loading loading-spinner loading-xs" />
                  )}
                  Crear sesión
                </button>
              )}
            </>
          )}
      </div>
      {scopeChoice !== null && (
        <ScopePromptModal
          event={event}
          isPending={enableSessions.isPending}
          onCancel={() => setScopeChoice(null)}
          onPick={(scope) => {
            setScopeChoice(null);
            enableSessions.mutate({ id: event.id, scope });
          }}
        />
      )}
    </div>
  );
}

/**
 * Modal de elección de scope para promover un evento recurrente a
 * sesiones. Aparece cuando el lead hace click en "Crear sesión" y el
 * evento tiene recurrencia (master propio o instancia Google).
 */
function ScopePromptModal({
  event,
  isPending,
  onCancel,
  onPick,
}: {
  event: CalendarEvent;
  isPending: boolean;
  onCancel: () => void;
  onPick: (scope: "single" | "series") => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card-elevated max-w-md w-full p-5 space-y-4"
      >
        <h3 className="font-display font-semibold text-lg">Crear sesión para</h3>
        <p className="text-sm text-base-content/70 break-words">
          <span className="font-medium">{event.title}</span> — evento recurrente.
        </p>
        <p className="text-sm text-base-content/65">
          ¿Querés trackear sólo esta fecha o todas las próximas?
        </p>
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={() => onPick("series")}
            disabled={isPending}
            className="btn btn-primary justify-start"
          >
            Todas las próximas (la serie)
            <span className="ml-auto text-xs opacity-70">recomendado</span>
          </button>
          <button
            type="button"
            onClick={() => onPick("single")}
            disabled={isPending}
            className="btn btn-ghost justify-start"
          >
            Solo este meet
            <span className="ml-auto text-xs opacity-60">
              {formatDate(event.date)}
            </span>
          </button>
        </div>
        <div className="flex justify-end pt-2 border-t border-base-300/60">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="btn btn-ghost btn-sm"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function priorityLabel(p: string): string {
  return (
    ({ urgent: "Urgente", high: "Alta", medium: "Media", low: "Baja" } as Record<string, string>)[
      p
    ] ?? p
  );
}

function priorityBadgeClass(p: string): string {
  return (
    (
      {
        urgent: "badge-error",
        high: "badge-warning",
        medium: "badge-info",
        low: "badge-ghost",
      } as Record<string, string>
    )[p] ?? "badge-ghost"
  );
}

function expandRange(startIso: string, endIso: string): string[] {
  const out: string[] = [];
  const d = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Grilla 7×6 (42 celdas) para el mes del `anchor`, empezando lunes. Las
 * celdas fuera del mes activo se incluyen para llenar la primera y última
 * semana — se distinguen visualmente con opacidad.
 */
function buildMonthGrid(anchor: Date): { year: number; month: number; day: number }[] {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const dayOfWeekMon = (firstOfMonth.getDay() + 6) % 7;
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - dayOfWeekMon);
  const cells: { year: number; month: number; day: number }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({ year: d.getFullYear(), month: d.getMonth(), day: d.getDate() });
  }
  return cells;
}
